/**
 * Development helpers to detect layout overflow inside the dashboard shell.
 */

const DEFAULT_SELECTOR =
  '.app-surface, .app-surface-raised, .app-kpi-card, .app-contained-panel, .app-table-wrap, .app-dual-chart__svg-wrap';

const DEFAULT_IGNORE = new Set([
  'app-main-scroll',
  'app-sidebar',
  'app-sidebar__nav',
]);

function isIgnored(el) {
  if (!el?.classList) return false;
  for (const cls of el.classList) {
    if (DEFAULT_IGNORE.has(cls)) return true;
  }
  return false;
}

/**
 * Returns elements whose content overflows their layout box (scrollWidth/Height > client).
 */
export function findOverflowingElements(root, { selector = DEFAULT_SELECTOR, max = 40 } = {}) {
  if (!root || typeof root.querySelectorAll !== 'function') return [];

  const hits = [];
  const nodes = root.querySelectorAll(selector);

  for (const el of nodes) {
    if (isIgnored(el)) continue;
    const sw = el.scrollWidth;
    const sh = el.scrollHeight;
    const cw = el.clientWidth;
    const ch = el.clientHeight;
    const horizontal = sw > cw + 1;
    const vertical = sh > ch + 1;
    if (!horizontal && !vertical) continue;

    hits.push({
      element: el,
      horizontal,
      vertical,
      scrollWidth: sw,
      clientWidth: cw,
      scrollHeight: sh,
      clientHeight: ch,
      tag: el.tagName?.toLowerCase(),
      className: el.className,
    });
    if (hits.length >= max) break;
  }

  return hits;
}

/**
 * Logs overflow warnings in development (console.warn).
 */
export function warnLayoutOverflow(root, options = {}) {
  if (process.env.NODE_ENV === 'production') return [];
  const hits = findOverflowingElements(root, options);
  if (!hits.length) return hits;

  // eslint-disable-next-line no-console
  console.warn(
    `[layout-containment] ${hits.length} container(s) have overflowing content`,
    hits.map((h) => ({
      tag: h.tag,
      className: h.className,
      horizontal: h.horizontal,
      vertical: h.vertical,
      deltaW: h.scrollWidth - h.clientWidth,
      deltaH: h.scrollHeight - h.clientHeight,
    }))
  );
  return hits;
}

/**
 * Validates that an element's scroll dimensions do not exceed its client box.
 */
export function assertContained(el, tolerancePx = 1) {
  if (!el) return { ok: true };
  const ok =
    el.scrollWidth <= el.clientWidth + tolerancePx &&
    el.scrollHeight <= el.clientHeight + tolerancePx;
  return {
    ok,
    scrollWidth: el.scrollWidth,
    clientWidth: el.clientWidth,
    scrollHeight: el.scrollHeight,
    clientHeight: el.clientHeight,
  };
}
